import { inject, Injectable, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart } from '@angular/router';
import type { IProductDataV2 } from '@df/catalog/product/product';
import { Meta } from '@df/core/meta/meta';
import { Status } from '@df/core/status';
import { Customer } from '@df/session/api/customer';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import head from 'lodash-es/head';
import uniq from 'lodash-es/uniq';
import { filter } from 'rxjs/internal/operators/filter';
import { skip } from 'rxjs/internal/operators/skip';

import type { ProductDecorator } from '../../../../model/product/product.decorator';

export enum WishlistState {
    BeforeAdd = 'beforeAdd',
    AddSuccess = 'addSuccess',
    RemoveSuccess = 'removeSuccess',
    Already = 'already'
}

export type WishlistMessage = 'login' | 'beforeAdd' | 'addSuccess' | 'removeSuccess' | 'already';

@Injectable()
export class ProductLayoutWishlistService {
    readonly state = signal<WishlistState | undefined>(undefined);
    readonly optionValue = signal<number | undefined>(undefined);
    readonly missingAttrCode = signal<string | undefined>(undefined);
    readonly callBack = signal<(() => void) | undefined>(undefined);

    readonly wishlistStatus = new Status();

    message = linkedSignal<WishlistMessage | undefined>(() => {
        switch (this.state()) {
            case WishlistState.BeforeAdd:
                return 'beforeAdd';
            case WishlistState.AddSuccess:
                if (this.customer.model.isLoggedIn) {
                    return 'addSuccess';
                }

                return 'login';
            case WishlistState.RemoveSuccess:
                return 'removeSuccess';
            case WishlistState.Already:
                return 'already';
        }
    });

    private readonly customer = inject(Customer);
    private readonly wishlist = inject(Wishlist);

    constructor(meta: Meta) {
        meta.routerEvents
            .pipe(
                skip(1),
                filter(e => e instanceof NavigationStart),
                takeUntilDestroyed()
            )
            .subscribe(() => this.reset());
    }

    /**
     * Add to wishlist
     */
    toggle(product: ProductDecorator<IProductDataV2>, cb: () => void) {
        this.wishlist.toggle(this.wishlistStatus, product.id, undefined, () => {
            this.reset();

            cb();
        });
    }

    /**
     * Remove from wishlist
     */
    remove(product: ProductDecorator<IProductDataV2>, cb?: () => void) {
        const itemId = head(this.wishlist.getItemsByProduct(product.id))?.data?.id;
        const items = itemId ? [itemId] : [];

        product.parent.childrenIds.map(id => {
            const _items = this.wishlist.getItemsByProduct(id)?.map(item => item.data.id);

            if (_items?.length) {
                items.push(..._items);
            }
        });

        this.wishlist.remove(this.wishlistStatus, uniq(items), () => {
            this.reset();

            cb?.();
        });
    }

    reset() {
        this.state.set(undefined);
        this.optionValue.set(undefined);
        this.missingAttrCode.set(undefined);

        this.wishlistStatus.reset();
    }
}
